import { useState, useEffect, useRef } from 'react'
import useAuthActions from 'store/actions/auth'
import Box from '@mui/material/Box'
import logo from 'assets/logos/logo_white.png'
import Input from './Input'
import Button from './Button'
import { withCountryCode, isAutofilled } from './phoneNumber'

const RECAPTCHA_ID = 'recaptcha-container'
// Firebase's texted codes are six digits.
const CODE_LENGTH = 6

// How long after a change to look for the browser's autofill mark, which
// Safari may set after the number lands.
const AUTOFILL_CHECK_MS = 100

const PhoneLoginForm = ({ signIn, verifyCode, error, isLoading, codeSent }) => {
  const [phone, setPhone] = useState('')
  const [code, setCode] = useState('')
  const { setRecaptcha } = useAuthActions()

  // Whether the number in the field was filled in or pasted rather than
  // typed: only such a number takes a country code, shown as soon as it
  // lands. A typed one is sent as typed.
  const phoneInput = useRef(null)
  const phoneFilledIn = useRef(false)
  const autofillCheck = useRef(null)

  const changePhone = (value) => {
    clearTimeout(autofillCheck.current)

    // A number with its + has its country code; the field shows the +.
    if (value.startsWith('+')) {
      phoneFilledIn.current = false
      setPhone(value.slice(1))
      return
    }

    // iOS doesn't fill a number in one change, so a change of more than one
    // character (a paste, a fill in one step) is one sign and the browser's
    // mark, looked for once the change has landed, the other.
    phoneFilledIn.current = Math.abs(value.length - phone.length) > 1
    setPhone(phoneFilledIn.current ? withCountryCode(value) : value)
    autofillCheck.current = setTimeout(() => {
      if (!isAutofilled(phoneInput.current)) return
      phoneFilledIn.current = true
      setPhone(withCountryCode)
    }, AUTOFILL_CHECK_MS)
  }

  const sendCode = () => {
    const filledIn = phoneFilledIn.current || isAutofilled(phoneInput.current)
    const number = filledIn ? withCountryCode(phone) : phone
    setPhone(number)
    signIn(number)
  }

  useEffect(() => {
    setRecaptcha(RECAPTCHA_ID)
  }, [setRecaptcha])

  useEffect(() => () => clearTimeout(autofillCheck.current), [])

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: '20px',
        position: 'relative',
        height: '400px',
        '& img': {
          position: 'absolute',
          top: '10px',
          width: '60px',
        },
      }}
    >
      <Box id={RECAPTCHA_ID} />
      <img src={logo} alt="logo" />
      <Box>
        {/* Keyed apart, so the code is a new field: a phone reads a field's
            autofill when it is focused, and offers no code to the phone
            number's field. */}
        {codeSent ? (
          <Input
            key="code"
            value={code}
            onChange={(value) => {
              setCode(value)
              // An autofilled code lands whole: sign in without a tap.
              if (value.replace(/\D/g, '').length === CODE_LENGTH && !isLoading)
                verifyCode(value)
            }}
            onSubmit={() => verifyCode(code)}
            submitLabel="sign in"
            belowText={error || 'enter code'}
            isLoading={isLoading}
            inputProps={{
              name: 'code',
              autoComplete: 'one-time-code',
              inputMode: 'numeric',
              enterKeyHint: 'go',
            }}
          />
        ) : (
          <Input
            key="phone"
            value={phone}
            onChange={changePhone}
            onSubmit={sendCode}
            submitLabel="send code"
            adornment="+"
            belowText={error || 'enter phone number with country code'}
            isLoading={isLoading}
            inputProps={{
              ref: phoneInput,
              name: 'phone',
              type: 'tel',
              autoComplete: 'tel',
              enterKeyHint: 'send',
            }}
          />
        )}
      </Box>
      <Box sx={{ position: 'absolute', bottom: '10px' }}>
        {codeSent && <Button onClick={() => signIn(phone)}>resend code</Button>}
      </Box>
    </Box>
  )
}

export default PhoneLoginForm
